// Metro-level closing-cost metadata powering /closing-costs/city/:city programmatic
// SEO pages. Bet 4. Reuses METRO_RATES slugs; derives avgCostPct from parent state
// with small metro tilts for high-tax cities (NYC, Philly, Chicago, SF, etc.).

import { METRO_RATES, type MetroRateInfo } from "./metrosMortgageRates";
import {
  getClosingCostBySlug,
  estimateClosingCostDollar,
  type StateClosingCostInfo,
} from "./statesClosingCosts";

export interface MetroClosingCostInfo {
  slug: string;
  city: string;
  metro: string;
  stateName: string;
  stateAbbr: string;
  stateSlug: string;
  medianHomePrice: number;
  medianLoanAmount: number;
  /** Effective closing-cost % for this metro (state pct + optional tilt). */
  avgCostPct: number;
  /** Sentence about metro/county transfer tax quirks (may override state text). */
  transferTaxNote: string;
  /** 1-sentence local read. */
  marketNote: string;
  /** Parent-state row for shared context. */
  state?: StateClosingCostInfo;
}

// Metro-level closing-cost pct tilt vs parent state. Positive = higher than state avg
// (usually driven by a local transfer/recordation tax like NYC RPTT or Philly's).
// Undefined metros inherit the state avg unchanged.
const METRO_PCT_TILT: Record<string, number> = {
  "new-york-ny": 1.2, // NYC RPTT + mansion tax + mortgage recording tax
  "philadelphia-pa": 0.8, // Philly local transfer tax stacks on state
  "chicago-il": 0.6, // Cook + Chicago stamps
  "san-francisco-ca": 0.9, // SF transfer tax on higher-priced homes
  "los-angeles-ca": 0.4, // LA Measure ULA on >$5M; modest average tilt
  "washington-dc": 0.3, // Recordation stacked with transfer
  "boston-ma": 0.1,
  "seattle-wa": 0.2, // WA REET graduated
  "honolulu-hi": 0.2, // Graduated conveyance tax
  "miami-fl": 0.1,
  "las-vegas-nv": 0.2, // Clark County higher transfer
  "baltimore-md": 0.4, // Baltimore stacks additional city transfer
  "atlanta-ga": 0.1,
  "new-orleans-la": 0.2, // Orleans documentary tax
};

// Metro-specific transfer tax note overrides. Any metro not listed inherits the
// parent state's transferTax sentence.
const METRO_TRANSFER_NOTE: Record<string, string> = {
  "new-york-ny": "New York City layers a Real Property Transfer Tax (1.0–2.625% depending on price) plus a mortgage recording tax on top of the state's 0.4% transfer tax and 1% mansion tax over $1M. Buyers of new construction may also pay the seller's transfer tax by contract.",
  "philadelphia-pa": "Philadelphia charges one of the highest local transfer taxes in the country — 3.278% city stacked on the 1% state tax, for a combined 4.278%. It's typically split 50/50, but often negotiated in the offer.",
  "chicago-il": "Chicago layers a city transfer tax ($5.25 per $500, buyer-paid) plus a Cook County stamp ($0.50 per $500, seller-paid) on top of the state's $1 per $1,000 rate. Chicago is exploring a graduated 'mansion tax' for higher-price homes.",
  "san-francisco-ca": "San Francisco charges a graduated documentary transfer tax that ranges from 0.68% up to 6% on ultra-luxury purchases — one of the highest in the country. On typical SF homes ($1M–$2M) it runs 0.75%. Berkeley and Oakland levy their own city transfer taxes.",
  "los-angeles-ca": "Los Angeles is subject to California's 0.11% documentary transfer tax plus the city's Measure ULA — a 4% transfer tax on sales $5M–$10M and 5.5% above $10M. Most median-priced LA transactions pay only the base state and county documentary tax.",
  "washington-dc": "DC charges a 1.1% recordation tax paid by the buyer (1.45% on transactions over $400K) plus a matching 1.1% transfer tax paid by the seller — for a combined 2.2–2.9% of price.",
  "seattle-wa": "Washington's Real Estate Excise Tax is graduated: 1.10% up to $525K, 1.28% up to $1.525M, 2.75% up to $3.025M, and 3.0% above. King County (Seattle) sales in higher tiers regularly hit the 2.75%+ bands.",
  "honolulu-hi": "Hawaii's conveyance tax runs 0.10% up to $600K, rising to 1.25% on luxury purchases. Non-resident buyers of non-primary residences pay elevated rates on the same graduated scale.",
  "baltimore-md": "Baltimore City stacks a 1.5% transfer tax on top of Maryland's 0.5% state transfer tax and 1.0% recordation tax — for a combined ~3.0% of price at closing, one of the highest in the country.",
  "new-orleans-la": "New Orleans levies a documentary transaction tax on real estate transfers within Orleans Parish (typically about $325 flat plus a small percentage) — Louisiana has no state transfer tax, so this is the only tax line for most closings.",
  "las-vegas-nv": "Clark County's Real Property Transfer Tax is $5.10 per $500 (about 1.02%) — higher than most Nevada counties. Typically paid by the seller by convention but negotiable in the offer.",
  "boston-ma": "Massachusetts charges a $4.56-per-$1,000 (0.456%) deed excise tax paid by the seller. Boston has no additional city transfer tax — the recent proposal for one has stalled in the legislature.",
  "atlanta-ga": "Georgia charges a $1-per-$1,000 (0.1%) state transfer tax plus a $1.50-per-$500 intangibles tax on the loan. Atlanta has no additional city transfer tax; DeKalb and Fulton counties are aligned with state rates.",
  "miami-fl": "Florida's documentary stamp tax runs $0.70 per $100 (0.7%) on the deed plus $0.35 per $100 on the mortgage. Miami-Dade County adds a small surtax on non-single-family transactions.",
};

function buildMetroClosingCostInfo(m: MetroRateInfo): MetroClosingCostInfo {
  const state = getClosingCostBySlug(m.stateSlug);
  const stateAvg = state?.avgCostPct ?? 2.4;
  const tilt = METRO_PCT_TILT[m.slug] ?? 0;
  const avgCostPct = +(stateAvg + tilt).toFixed(2);
  const transferTaxNote =
    METRO_TRANSFER_NOTE[m.slug] ?? state?.transferTax ?? `${m.stateName} sets the transfer/recordation tax rules for the ${m.metro} area — see the state closing-costs page for full detail.`;
  const marketNote = tilt > 0
    ? `${m.city} sits above the ${m.stateName} state average because local transfer or recordation taxes stack on top of state rates.`
    : `${m.city} closing costs generally track the ${m.stateName} state average, with variance driven by lender fees, title insurance, and county recording fees.`;
  return {
    slug: m.slug,
    city: m.city,
    metro: m.metro,
    stateName: m.stateName,
    stateAbbr: m.stateAbbr,
    stateSlug: m.stateSlug,
    medianHomePrice: m.medianHomePrice,
    medianLoanAmount: m.medianLoanAmount,
    avgCostPct,
    transferTaxNote,
    marketNote,
    state,
  };
}

export const METRO_CLOSING_COSTS: MetroClosingCostInfo[] = METRO_RATES.map(buildMetroClosingCostInfo);

export function getMetroClosingCostBySlug(slug: string | undefined): MetroClosingCostInfo | undefined {
  if (!slug) return undefined;
  return METRO_CLOSING_COSTS.find((m) => m.slug === slug.toLowerCase());
}

export function getSiblingMetroClosingCosts(slug: string, count = 3): MetroClosingCostInfo[] {
  const i = METRO_CLOSING_COSTS.findIndex((m) => m.slug === slug);
  if (i === -1) return [];
  const out: MetroClosingCostInfo[] = [];
  for (let k = 1; k <= count; k++) out.push(METRO_CLOSING_COSTS[(i + k) % METRO_CLOSING_COSTS.length]);
  return out;
}

export function getMetroClosingCostsByState(stateSlug: string): MetroClosingCostInfo[] {
  return METRO_CLOSING_COSTS.filter((m) => m.stateSlug === stateSlug);
}

export function estimateMetroClosingCostDollar(info: MetroClosingCostInfo): number {
  return Math.round((info.medianHomePrice * info.avgCostPct) / 100);
}

// Re-export the shared state helper so callers only need to import from one module.
export { estimateClosingCostDollar };